import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  fetchReminderAudit,
  fetchReminderControls,
  fetchReminderCycle,
  fetchReminderExplain,
  fetchReminderPlan,
  fetchReminderSent,
  recallReminders,
  sendReminderTest,
  updateReminderControls
} from 'api'
import { confirmDialog, showToast } from 'app/helpers'

// Gordon — the HQ super-admin cockpit for the reminder engine.
//
// The engine is plugin-based: the backend registry (services.reminder_registry)
// returns one "plugin" per stream (Program Material, Interaction Audit, ...) and
// this page renders one card each. A card carries the plugin's on/off toggle,
// its env-gate state, its schedule/escalation copy, any plugin-specific knobs
// (Program Material has an editable opener + deadline), and a test-send.
// Above them sits the master Stop-all / Resume-all kill switch. The Agent Audit
// tab pages one plugin's send ledger, chosen from a dropdown.

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2, 3), maxWidth: 900 },
  title: { fontWeight: 700, fontSize: 18, marginBottom: theme.spacing(1) },
  subtitle: { color: theme.palette.text.secondary, fontSize: 13, marginBottom: theme.spacing(2) },
  tabsBar: { marginBottom: theme.spacing(2), borderBottom: '2px solid #eee', minHeight: 40 },
  section: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  sectionTitle: { fontWeight: 600, fontSize: 14, marginBottom: theme.spacing(1) },
  pluginHead: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginBottom: theme.spacing(0.5), flexWrap: 'wrap' },
  pluginName: { fontWeight: 700, fontSize: 15 },
  pluginDesc: { color: theme.palette.text.secondary, fontSize: 12.5, marginBottom: theme.spacing(1.5) },
  listRow: {
    padding: theme.spacing(1.5, 2),
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    cursor: 'pointer',
    transition: 'background 0.12s',
    '&:hover': { background: '#f7f7f7' }
  },
  listMain: { minWidth: 0 },
  listName: { fontWeight: 700, fontSize: 14.5 },
  listDesc: { color: theme.palette.text.secondary, fontSize: 12, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  listRight: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), flexShrink: 0 },
  chevron: { color: theme.palette.text.secondary, fontSize: 22, lineHeight: 1, marginLeft: theme.spacing(0.5) },
  monthBar: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), padding: theme.spacing(1, 1.5), background: '#f4f7fb', border: '1px solid #e3ebf5', borderRadius: 6, marginBottom: theme.spacing(1.5), flexWrap: 'wrap' },
  logicHead: { fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4, color: theme.palette.text.secondary, margin: theme.spacing(1, 0, 0.5) },
  logicItem: { fontSize: 13, marginBottom: theme.spacing(0.75), display: 'flex', gap: theme.spacing(1) },
  msgBlock: { border: '1px solid #eee', borderRadius: 6, marginBottom: theme.spacing(1.5), overflow: 'hidden' },
  msgHead: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), padding: theme.spacing(1, 1.5), background: '#fafafa', borderBottom: '1px solid #eee', flexWrap: 'wrap' },
  msgTitle: { fontWeight: 700, fontSize: 13 },
  msgWhen: { color: theme.palette.text.secondary, fontSize: 12 },
  msgText: { margin: 0, padding: theme.spacing(1.5), whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5, lineHeight: 1.5 },
  slackChip: { backgroundColor: '#e8eef9', color: '#33478a', fontWeight: 600 },
  planBar: { display: 'flex', alignItems: 'center', gap: theme.spacing(1.5), marginBottom: theme.spacing(2), flexWrap: 'wrap' },
  planNote: { padding: theme.spacing(1.25, 1.5), borderRadius: 6, fontSize: 13, marginBottom: theme.spacing(2) },
  planWarn: { background: '#fff8e1', border: '1px solid #ffe0a3' },
  planStop: { background: '#fdecea', border: '1px solid #f5c6cb' },
  nowrap: { whiteSpace: 'nowrap' },
  threadLink: { color: '#33478a', fontSize: 12, textDecoration: 'underline', cursor: 'pointer', display: 'inline-block' },
  planOk: { background: '#e8f5e9', border: '1px solid #c8e6c9' },
  planQuiet: { color: theme.palette.text.secondary, fontSize: 13, padding: theme.spacing(2) },
  railPhase: {
    padding: theme.spacing(0.75, 2),
    background: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: 11,
    letterSpacing: '0.09em',
    textTransform: 'uppercase',
    color: theme.palette.text.secondary,
    fontWeight: 700
  },
  railDay: {
    display: 'grid',
    gridTemplateColumns: '112px 10px 1fr auto',
    gap: theme.spacing(2),
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    textAlign: 'left',
    width: '100%',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    font: 'inherit'
  },
  railToday: { background: 'rgba(242, 94, 53, 0.08)' },
  railDate: { fontSize: 13, color: theme.palette.text.secondary },
  railDot: { width: 9, height: 9, borderRadius: '50%', justifySelf: 'center' },
  railSent: { background: theme.palette.success.main },
  railPlanned: { border: `1.5px solid ${theme.palette.grey[400]}` },
  railWhat: { fontSize: 14 },
  railCount: {
    fontSize: 12.5,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap'
  },
  railFailed: { color: theme.palette.error.main, fontWeight: 600 },
  emailChip: { backgroundColor: '#f3e8f9', color: '#6a3a8a', fontWeight: 600 },
  metaLine: { color: theme.palette.text.secondary, fontSize: 12, marginTop: 2 },
  killBanner: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 6,
    marginBottom: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2)
  },
  killPaused: { background: '#fdecea', border: '1px solid #f5c6cb' },
  killActive: { background: '#e8f5e9', border: '1px solid #c8e6c9' },
  killLabelPaused: { color: '#b71c1c', fontWeight: 700 },
  killLabelActive: { color: '#2e7d32', fontWeight: 700 },
  row: { display: 'flex', alignItems: 'center', gap: theme.spacing(2), marginBottom: theme.spacing(1.5), flexWrap: 'wrap' },
  label: { minWidth: 150, fontWeight: 600, fontSize: 13 },
  hint: { color: theme.palette.text.secondary, fontSize: 12 },
  templateBox: { width: '100%' },
  actions: { display: 'flex', gap: theme.spacing(1), marginTop: theme.spacing(1) },
  saveBar: { display: 'flex', gap: theme.spacing(2), alignItems: 'center', marginTop: theme.spacing(1.5) },
  envChip: { backgroundColor: '#fff3e0', color: '#8a5a00', fontWeight: 600 },
  onChip: { backgroundColor: '#e3f2e1', color: '#2e6b2b', fontWeight: 600 },
  offChip: { backgroundColor: '#eeeeee', color: '#616161', fontWeight: 600 },
  deliveredChip: { backgroundColor: '#e3f2e1', color: '#2e6b2b', fontWeight: 600 },
  errorChip: { backgroundColor: '#fbe3e1', color: '#a6392a', fontWeight: 600 },
  preview: { maxWidth: 280, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  pager: { display: 'flex', alignItems: 'center', gap: theme.spacing(1), marginTop: theme.spacing(1.5) },
  muted: { color: theme.palette.text.secondary }
}))

const PAGE_SIZE = 50

// ---- "How this works" popup -----------------------------------------------

const LogicDialog = ({ plugin, open, onClose }) => {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [month, setMonth] = useState('')

  // Start from the stream's default cycle each time the popup opens.
  useEffect(() => {
    setMonth('')
  }, [open, plugin.key])

  useEffect(() => {
    if (!open) return undefined
    let alive = true
    setLoading(true)
    setData(null)
    fetchReminderExplain(plugin.key, month)
      .then((res) => {
        if (alive) setData(res)
      })
      .catch(() => {
        if (alive) showToast('Could not load the reminder logic', 'error')
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
    }
  }, [open, plugin.key, month])

  const months = data?.months || []
  const selectedMonth = month || months[0]?.value || ''

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>{plugin.label} — how this works</DialogTitle>
      <DialogContent dividers>
        {loading || !data ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {months.length > 0 && (
              <div className={classes.monthBar}>
                <span className={classes.label}>Preview for</span>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setMonth(e.target.value)}
                  variant="outlined"
                  margin="dense"
                  style={{ minWidth: 170 }}
                >
                  {months.map((m) => (
                    <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
                  ))}
                </Select>
                {data.preview_deadline && (
                  <span className={classes.hint}>deadline {data.preview_deadline}</span>
                )}
              </div>
            )}

            <Typography className={classes.logicHead}>The logic</Typography>
            {(data.how_it_works || []).map((line, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className={classes.logicItem}>
                <span>•</span>
                <span>{line}</span>
              </div>
            ))}

            <Typography className={classes.logicHead}>
              What we send — exact messages
            </Typography>
            {(data.messages || []).map((m, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className={classes.msgBlock}>
                <div className={classes.msgHead}>
                  <span className={classes.msgTitle}>{m.title}</span>
                  <Chip
                    size="small"
                    label={m.channel}
                    className={m.channel === 'Email' ? classes.emailChip : classes.slackChip}
                  />
                  {m.when && <span className={classes.msgWhen}>· {m.when}</span>}
                </div>
                <pre className={classes.msgText}>{m.text || '—'}</pre>
              </div>
            ))}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} style={{ textTransform: 'none' }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ---- one plugin's detail view ---------------------------------------------

/**
 * The only question worth answering above the fold: is this stream reaching
 * anybody right now, and if not, why not? The four things that can silence it —
 * master pause, stream toggle, environment flag, missing token — and the
 * override that redirects everything to one DM used to live in four different
 * places, so the page could not be read for the answer.
 */
const LiveBanner = ({ plugin }) => {
  const classes = useStyles()
  const state = plugin.live_state || 'off'
  const tone =
    state === 'live' ? classes.planOk : state === 'test' ? classes.planWarn : classes.planStop
  const heading =
    state === 'live'
      ? 'Sending for real'
      : state === 'test'
      ? 'Test mode — nobody but the test recipient sees these'
      : 'Not sending'
  return (
    <div className={`${classes.planNote} ${tone}`}>
      <b>{heading}.</b> {plugin.live_explanation}
    </div>
  )
}


const PluginDetail = ({ plugin, disabled, onSaved, onBack }) => {
  const classes = useStyles()
  const settings = plugin.settings || {}
  // Both are driven by what the plugin publishes, not by its name: a stream
  // gets a Message box when it ships a default_template, and the day fields
  // only when it actually runs a monthly cycle.
  const hasTemplate = typeof settings.default_template === 'string'
  const hasSchedule = settings.default_start_day != null
  const hasEmailSwitch = typeof settings.emails_enabled === 'boolean'

  const [enabled, setEnabled] = useState(!!plugin.enabled)
  const [emailsEnabled, setEmailsEnabled] = useState(true)
  const [template, setTemplate] = useState('')
  const [deadlineDay, setDeadlineDay] = useState('')
  const [startDay, setStartDay] = useState('')
  const [saving, setSaving] = useState(false)
  const [testRecipient, setTestRecipient] = useState('')
  const [testing, setTesting] = useState(false)
  const [showLogic, setShowLogic] = useState(false)

  const defaultTemplate = settings.default_template || ''

  // Re-hydrate whenever the parent hands down a fresh plugin snapshot.
  useEffect(() => {
    setEnabled(!!plugin.enabled)
    if (hasTemplate) {
      setTemplate(settings.message_template || settings.default_template || '')
    }
    if (hasSchedule) {
      setDeadlineDay(settings.deadline_day == null ? '' : String(settings.deadline_day))
      setStartDay(settings.start_day == null ? '' : String(settings.start_day))
    }
    if (hasEmailSwitch) setEmailsEnabled(!!settings.emails_enabled)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plugin])

  const save = async () => {
    setSaving(true)
    try {
      const payload = { plugin: plugin.key, enabled }
      if (hasTemplate) {
        // A reset-to-default / unchanged template is stored blank, so the page
        // keeps tracking the code default instead of freezing a copy of it.
        const trimmed = (template || '').trim()
        payload.message_template =
          trimmed && trimmed !== (defaultTemplate || '').trim() ? template : ''
      }
      if (hasSchedule) {
        payload.deadline_day = deadlineDay === '' ? null : Number(deadlineDay)
        payload.start_day = startDay === '' ? null : Number(startDay)
      }
      if (hasEmailSwitch) payload.emails_enabled = emailsEnabled
      onSaved(await updateReminderControls(payload))
      showToast(`${plugin.label} saved`, 'success')
    } catch (error) {
      showToast(error?.response?.data?.detail || 'Could not save the plugin', 'error')
    } finally {
      setSaving(false)
    }
  }

  const sendTest = async () => {
    setTesting(true)
    try {
      const res = await sendReminderTest(plugin.key, testRecipient)
      showToast(`Test sent to ${res.recipient}`, 'success')
    } catch (error) {
      showToast(error?.response?.data?.detail || 'Could not send the test message', 'error')
    } finally {
      setTesting(false)
    }
  }

  return (
    <>
      <Button
        size="small"
        onClick={onBack}
        style={{ textTransform: 'none', marginBottom: 8, paddingLeft: 0 }}
      >
        ← All streams
      </Button>
      <Paper variant="outlined" className={classes.section}>
      <div className={classes.pluginHead}>
        <Typography className={classes.pluginName}>{plugin.label}</Typography>
        <Chip
          size="small"
          label={enabled ? 'On' : 'Off'}
          className={enabled ? classes.onChip : classes.offChip}
        />
        {!plugin.env_enabled && (
          <Tooltip
            arrow
            title="This stream is turned off at the environment level (its enable flag is not set), so nothing is sent even when it is On here. Ops must enable it on the server."
          >
            <Chip size="small" label="Off in this environment" className={classes.envChip} />
          </Tooltip>
        )}
      </div>
      <Typography className={classes.pluginDesc}>{plugin.description}</Typography>

      <Button
        size="small"
        variant="outlined"
        onClick={() => setShowLogic(true)}
        style={{ textTransform: 'none', marginBottom: 12 }}
      >
        How this works
      </Button>
      <LogicDialog plugin={plugin} open={showLogic} onClose={() => setShowLogic(false)} />

      <div className={classes.row}>
        <span className={classes.label}>Reminders</span>
        <FormControlLabel
          control={
            <Switch
              color="primary"
              checked={enabled}
              disabled={disabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
          }
          label={enabled ? 'On' : 'Off'}
        />
        <span className={classes.hint}>Turns this stream on or off.</span>
      </div>

      {hasEmailSwitch && (
        <div className={classes.row}>
          <span className={classes.label}>Escalation email</span>
          <Switch
            checked={emailsEnabled}
            onChange={(e) => setEmailsEnabled(e.target.checked)}
            color="primary"
            disabled={disabled || !settings.emails_env_enabled}
          />
          <span className={classes.hint}>
            {settings.emails_env_enabled
              ? settings.emails_help
              : 'Switched off in this environment — nothing can send it, whatever this says.'}
            {settings.emails_recipient
              ? ` Goes to ${settings.emails_recipient}.`
              : ' No recipient is configured, so it cannot send.'}
          </span>
        </div>
      )}

      <Typography className={classes.metaLine}>
        <strong>Schedule:</strong> {plugin.schedule || '—'}
      </Typography>
      <Typography className={classes.metaLine} gutterBottom>
        <strong>Escalation:</strong> {plugin.escalation || '—'}
      </Typography>

      {hasTemplate && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <Typography className={classes.sectionTitle}>Message — first opener</Typography>
          <Typography className={classes.hint} gutterBottom>
            {settings.template_help}
            {(settings.template_tokens || []).length ? (
              <>
                {' '}Tokens{' '}
                {(settings.template_tokens || []).map((token, i, all) => (
                  <span key={token}>
                    <code>{token}</code>
                    {i < all.length - 1 ? ', ' : ''}
                  </span>
                ))}{' '}
                are filled in automatically.
              </>
            ) : null}
          </Typography>
          <TextField
            className={classes.templateBox}
            multiline
            minRows={4}
            variant="outlined"
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
          />
          <div className={classes.actions}>
            <Button
              size="small"
              variant="outlined"
              style={{ textTransform: 'none' }}
              onClick={() => setTemplate(defaultTemplate)}
              disabled={template === defaultTemplate}
            >
              Reset to default
            </Button>
          </div>

        </>
      )}

      {hasSchedule && (
        <>
          <Divider style={{ margin: '12px 0' }} />
          <div className={classes.row}>
            <span className={classes.label}>Start day</span>
            <TextField
              type="number"
              size="small"
              variant="outlined"
              value={startDay}
              onChange={(e) => setStartDay(e.target.value)}
              inputProps={{ min: 1, max: 31 }}
              style={{ width: 110 }}
              placeholder={String(settings.default_start_day)}
            />
            <span className={classes.hint}>
              Day of the month the cycle opens. Nothing is said before it — no
              budget request, no centre reminder (blank = default,{' '}
              {settings.default_start_day}).
            </span>
          </div>

          <div className={classes.row}>
            <span className={classes.label}>Deadline day</span>
            <TextField
              type="number"
              size="small"
              variant="outlined"
              value={deadlineDay}
              onChange={(e) => setDeadlineDay(e.target.value)}
              inputProps={{ min: 1, max: 31 }}
              style={{ width: 110 }}
              placeholder={String(settings.default_deadline_day)}
            />
            <span className={classes.hint}>
              Day of the month the order is due (blank = default,{' '}
              {settings.default_deadline_day}, clamped to month end).
            </span>
          </div>

        </>
      )}

      <Divider style={{ margin: '12px 0' }} />
      <Typography className={classes.sectionTitle}>Test send</Typography>
      <div className={classes.row}>
        <span className={classes.label}>Recipient</span>
        <TextField
          size="small"
          variant="outlined"
          value={testRecipient}
          onChange={(e) => setTestRecipient(e.target.value)}
          placeholder="Me (default) — or a Slack user ID"
          style={{ width: 280 }}
        />
        <Button
          variant="outlined"
          color="primary"
          style={{ textTransform: 'none' }}
          onClick={sendTest}
          disabled={testing}
        >
          {testing ? 'Sending…' : 'Send'}
        </Button>
      </div>
      <Typography className={classes.hint}>
        Renders this stream&apos;s current message and DMs it only to that one
        person, tagged <strong>[TEST — please ignore]</strong>. It never goes to a
        centre channel. Leave the recipient blank to send it to the configured
        test DM.
      </Typography>

      <div className={classes.saveBar}>
        <Button
          variant="contained"
          color="primary"
          style={{ textTransform: 'none', minWidth: 120 }}
          onClick={save}
          disabled={saving}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </div>
      </Paper>
    </>
  )
}

// LOCAL PREVIEW ONLY (vite dev server; never true in any build): the plugin
// list is whatever the backend registry returns, so a stream that has not
// deployed yet is invisible here. Until the cash-register branch reaches this
// environment's backend, append its card read-only so the copy and cadence can
// be reviewed; the real card replaces it the moment the API returns the key.
const CASH_DEPOSIT_PREVIEW = {
  _preview: true,
  key: 'cash_deposit',
  label: 'Cash Deposits',
  description:
    'Chases each centre to bank the cash and cheques it has collected ' +
    '(Orders → Collect) — a daily nudge in the centre’s Slack channel while ' +
    'anything is in hand, and a Finance digest once the oldest crosses the ' +
    'age threshold.',
  enabled: true,
  // The state this stream WAKES UP in once deployed to dev: the dev.py flag is
  // already on and the override sits at its base default (Satya's DM) — so the
  // card previews Test mode, not off.
  env_enabled: true,
  schedule:
    'Daily 10:00 IST to each centre holding cash; Finance digest 10:30 IST ' +
    'for anything older than 3 days.',
  escalation:
    'Centres whose oldest in-hand cash is 3+ days old are named in one ' +
    'worst-first digest to Finance.',
  live_state: 'test',
  live_explanation:
    'Preview — not on this backend yet. The moment the cash-register deploy ' +
    'lands, this stream starts in Test mode: every message goes to one DM ' +
    '(U08474W7L1Y), not to the centres.',
  settings: {},
  messages: [
    {
      title: 'Daily deposit nudge',
      when: 'Daily 10:00 IST while cash is in hand',
      text:
        '💰 Cash to deposit — your centre\n' +
        '₹58,420 across 4 collection(s) is in hand. The oldest has been ' +
        'sitting for 6 days.\n' +
        'Please deposit it at the bank today.\n' +
        'Mark it deposited (challan photo/PDF attached): open the Cash Register'
    },
    {
      title: 'Finance escalation digest',
      when: 'Daily 10:30 IST once 3+ days old',
      text:
        '🔴 Cash sitting undeposited past 3 days — 1 centre(s)\n' +
        '• Sample Centre — ₹58,420 (4 entries, oldest 6d)\n' +
        'Open the Cash Register'
    }
  ]
}

const injectCashPreview = (res) => {
  if (!import.meta.env.DEV || !res) return res
  if ((res.plugins || []).some((p) => p.key === CASH_DEPOSIT_PREVIEW.key)) {
    return res
  }
  return { ...res, plugins: [...(res.plugins || []), CASH_DEPOSIT_PREVIEW] }
}

// Read-only detail for the preview card — the real editor talks to endpoints
// this environment's backend does not have yet.
const PreviewDetail = ({ plugin, onBack }) => {
  const classes = useStyles()
  return (
    <Paper variant="outlined" style={{ padding: 16 }}>
      <Button size="small" onClick={onBack} style={{ textTransform: 'none' }}>
        ‹ All streams
      </Button>
      <Typography variant="h6" style={{ fontWeight: 700, marginTop: 8 }}>
        {plugin.label}
      </Typography>
      <Typography className={classes.hint}>{plugin.description}</Typography>
      <Typography className={classes.hint}>
        <b>When:</b> {plugin.schedule}
      </Typography>
      <Typography className={classes.hint}>
        <b>Escalation:</b> {plugin.escalation}
      </Typography>
      {(plugin.messages || []).map((m) => (
        <div key={m.title} style={{ marginTop: 12 }}>
          <Typography variant="body2">
            <b>{m.title}</b> · {m.when}
          </Typography>
          <pre
            style={{
              background: '#fafafa',
              border: '1px solid #eee',
              borderRadius: 6,
              padding: 10,
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
              fontSize: 13,
              margin: '4px 0 0'
            }}
          >
            {m.text}
          </pre>
        </div>
      ))}
      <Typography className={classes.hint} style={{ marginTop: 12 }}>
        Controls (toggle, test-send, audit) activate once the cash-register
        deploy reaches this environment — the card then comes from the live
        registry instead of this preview.
      </Typography>
    </Paper>
  )
}

// ---- the plugin list (master view) ----------------------------------------

const PluginList = ({ plugins, onOpen }) => {
  const classes = useStyles()
  return (
    <div>
      {(plugins || []).map((plugin) => (
        <Paper
          key={plugin.key}
          variant="outlined"
          className={classes.listRow}
          onClick={() => onOpen(plugin.key)}
        >
          <div className={classes.listMain}>
            <Typography className={classes.listName}>{plugin.label}</Typography>
            <Typography className={classes.listDesc}>{plugin.description}</Typography>
            {plugin.live_explanation ? (
              <Typography className={classes.listDesc}>
                <b>{plugin.live_explanation}</b>
              </Typography>
            ) : null}
          </div>
          <div className={classes.listRight}>
            <Chip
              size="small"
              label={
                plugin.live_state === 'live'
                  ? 'Sending'
                  : plugin.live_state === 'test'
                  ? 'Test mode'
                  : 'Not sending'
              }
              className={plugin.live_state === 'live' ? classes.onChip : classes.offChip}
            />
            {!plugin.env_enabled && (
              <Chip size="small" label="Off in this environment" className={classes.envChip} />
            )}
            <span className={classes.chevron}>›</span>
          </div>
        </Paper>
      ))}
    </div>
  )
}

// ---- Controls tab ----------------------------------------------------------

const ControlsTab = () => {
  const classes = useStyles()
  const [controls, setControls] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [selectedKey, setSelectedKey] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      setControls(injectCashPreview(await fetchReminderControls()))
    } catch (error) {
      showToast('Could not load the reminder controls (HQ access required)', 'error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const paused = !!controls?.paused

  const toggleMaster = async (nextPaused) => {
    // The kill switch takes effect immediately (its own save), so "stop
    // everything now" is one click and does not wait on the other edits.
    setSaving(true)
    try {
      setControls(injectCashPreview(await updateReminderControls({ paused: nextPaused })))
      showToast(nextPaused ? 'All reminders paused' : 'Reminders resumed', 'success')
    } catch (error) {
      showToast('Could not change the master switch', 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    )
  }
  if (!controls) {
    return <Typography className={classes.muted}>Nothing to show.</Typography>
  }

  const plugins = controls.plugins || []
  const selected = selectedKey ? plugins.find((p) => p.key === selectedKey) : null

  // Detail view for one stream — no master banner here, keep the focus on the
  // stream being edited.
  if (selected && selected._preview) {
    return (
      <PreviewDetail plugin={selected} onBack={() => setSelectedKey(null)} />
    )
  }
  if (selected) {
    return (
      <>
        <LiveBanner plugin={selected} />
        {/* The stop-everything switch used to exist only on the list, one back
            link away. If something is going wrong you are looking at the stream
            it is going wrong on, so it belongs here too. */}
        <div className={`${classes.planNote} ${paused ? classes.planStop : classes.planWarn}`}>
          {paused
            ? 'Every stream is paused. Nothing will be sent until you resume.'
            : 'Something going wrong? This stops every Gordon message immediately — no deploy needed.'}
          <Button
            size="small"
            variant="contained"
            disableElevation
            onClick={() => toggleMaster(!paused)}
            disabled={saving}
            style={{
              textTransform: 'none',
              marginLeft: 12,
              backgroundColor: paused ? '#2e7d32' : '#c62828',
              color: '#fff'
            }}
          >
            {paused ? 'Resume all' : 'Stop all reminders'}
          </Button>
        </div>
        <PluginDetail
          plugin={selected}
          disabled={saving}
          onSaved={(res) => setControls(injectCashPreview(res))}
          onBack={() => setSelectedKey(null)}
        />
      </>
    )
  }

  return (
    <div>
      {/* Master kill switch */}
      <div className={`${classes.killBanner} ${paused ? classes.killPaused : classes.killActive}`}>
        <div>
          <Typography className={paused ? classes.killLabelPaused : classes.killLabelActive}>
            {paused ? 'All reminders paused' : 'Reminders active'}
          </Typography>
          <Typography className={classes.hint}>
            The master switch stops every stream&apos;s Slack sends at once — daily
            nudges and escalations. It applies before anything is sent.
          </Typography>
        </div>
        <Button
          variant="contained"
          disableElevation
          onClick={() => toggleMaster(!paused)}
          disabled={saving}
          style={{
            textTransform: 'none',
            backgroundColor: paused ? '#2e7d32' : '#c62828',
            color: '#fff',
            minWidth: 140
          }}
        >
          {paused ? 'Resume all' : 'Stop all'}
        </Button>
      </div>

      <PluginList plugins={plugins} onOpen={setSelectedKey} />

      {controls.updated_at && (
        <Typography className={classes.hint}>
          Last updated {dayjs(controls.updated_at).format('D MMM YYYY, HH:mm')}
          {controls.updated_by ? ` by ${controls.updated_by}` : ''}
        </Typography>
      )}
    </div>
  )
}

// ---- Agent Audit tab -------------------------------------------------------

const StageChip = ({ stage }) => (
  <Chip size="small" label={stage} variant="outlined" style={{ textTransform: 'capitalize' }} />
)

const DeliveredCell = ({ row }) => {
  const classes = useStyles()
  if (row.delivered) {
    return <Chip size="small" label="Delivered" className={classes.deliveredChip} />
  }
  return (
    <Tooltip title={row.error || 'Not delivered'} arrow>
      <Chip size="small" label={row.error ? 'Failed' : 'Not sent'} className={classes.errorChip} />
    </Tooltip>
  )
}

// "All" heads every stream picker (Satya, 23 Oct): every stream in one list,
// each row saying which stream it came from.
const ALL_STREAMS = 'all'
const withAll = (plugins) => [
  { key: ALL_STREAMS, label: 'All' },
  ...(plugins || [])
]

const AuditTab = ({ plugins }) => {
  const classes = useStyles()
  const options = withAll(plugins)
  const [plugin, setPlugin] = useState(ALL_STREAMS)
  const isAll = plugin === ALL_STREAMS
  const [data, setData] = useState({ results: [], count: 0 })
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)

  // The plugin list is fetched by the page and may arrive after this tab has
  // mounted; adopt the first stream once it does so the dropdown is never stuck
  // empty.
  useEffect(() => {
    if (!plugin && options.length) setPlugin(options[0].key)
  }, [options, plugin])

  const load = useCallback(async (key, p) => {
    if (!key) return
    setLoading(true)
    try {
      setData(await fetchReminderAudit({ plugin: key, limit: PAGE_SIZE, offset: p * PAGE_SIZE }))
    } catch (error) {
      setData({ results: [], count: 0 })
      showToast('Could not load the agent audit (HQ access required)', 'error')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(plugin, page)
  }, [plugin, page, load])

  const totalPages = useMemo(() => Math.max(1, Math.ceil((data.count || 0) / PAGE_SIZE)), [data.count])

  return (
    <div>
      <div className={classes.row}>
        <span className={classes.label}>Stream</span>
        <Select
          value={plugin}
          onChange={(e) => {
            setPage(0)
            setPlugin(e.target.value)
          }}
          variant="outlined"
          margin="dense"
          style={{ minWidth: 220 }}
        >
          {options.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  {isAll ? <TableCell>Stream</TableCell> : null}
                  <TableCell>Centre</TableCell>
                  <TableCell>Stage</TableCell>
                  <TableCell>Destination</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell>Message</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.results.map((row) => (
                  <TableRow key={row.uuid} hover>
                    <TableCell>{row.date}</TableCell>
                    {isAll ? <TableCell>{row.stream}</TableCell> : null}
                    <TableCell>{row.centre}</TableCell>
                    <TableCell><StageChip stage={row.stage} /></TableCell>
                    <TableCell>{row.destination || '—'}</TableCell>
                    <TableCell align="center"><DeliveredCell row={row} /></TableCell>
                    <TableCell>
                      <Tooltip title={row.preview || ''} arrow>
                        <span className={classes.preview}>{row.preview || '—'}</span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {data.results.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={isAll ? 7 : 6}>
                      <Typography variant="body2" className={classes.muted}>
                        No reminders have been sent yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <div className={classes.pager}>
            <Button
              size="small"
              variant="outlined"
              style={{ textTransform: 'none' }}
              disabled={page <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              Previous
            </Button>
            <Typography variant="body2" className={classes.muted}>
              Page {page + 1} of {totalPages} · {data.count} row{data.count === 1 ? '' : 's'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              style={{ textTransform: 'none' }}
              disabled={page + 1 >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </>
      )}
    </div>
  )
}

// ---- What's next -----------------------------------------------------------
//
// A forecast, not a log. The Agent Audit tab answers "what went out"; this one
// answers "what is about to" — the only question that can stop a mistake before
// ten centre channels see it. Everything here is rendered by the same functions
// the scheduled job calls, so it cannot quietly drift from what will be sent.

/**
 * "Already sent" — the undo lane.
 *
 * The plan tab stops a mistake before it happens. This one is for after: the
 * copy that actually went out, and a way to edit or delete it in Slack without
 * leaving Alfred. Bulk-first on purpose — the failure this exists for is one
 * wrong opener landing in ten centre channels in the same minute.
 */
const SentTab = ({ plugins }) => {
  const classes = useStyles()
  const options = withAll(plugins)
  const [stream, setStream] = useState(ALL_STREAMS)
  const isAll = stream === ALL_STREAMS
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [picked, setPicked] = useState([])
  const [showAll, setShowAll] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const [editText, setEditText] = useState('')
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    if (!stream) return
    setLoading(true)
    fetchReminderSent({ plugin: stream })
      .then((data) => {
        setRows(data?.results || [])
        setPicked([])
      })
      .catch(() => showToast('Could not load what has been sent', 'error'))
      .finally(() => setLoading(false))
  }, [stream])

  // The plugin list is fetched by the page and may arrive after this tab has
  // mounted; adopt the first stream once it does so the dropdown is never stuck
  // empty. Same as the audit tab.
  useEffect(() => {
    if (!stream && options.length) setStream(options[0].key)
  }, [options, stream])

  useEffect(() => {
    load()
  }, [load])

  // Messages sent before the ledger stored a channel cannot be touched, and
  // they are most of the history. Showing them by default buries the handful
  // that can actually be acted on, so they are behind a toggle.
  // Grouped by centre, newest first inside each. Ten centres times a month of
  // messages is unreadable as one flat date list — you want to see one centre's
  // conversation as a block and know where it has got to.
  const shown = useMemo(() => {
    // A send that FAILED is shown by default even though it cannot be changed —
    // a centre that silently stopped receiving reminders is the thing you most
    // need to notice, and hiding it behind a toggle is how it stays unnoticed.
    const list = showAll
      ? rows
      : rows.filter((r) => r.can_recall || !r.delivered)
    return [...list].sort(
      (a, b) =>
        String(a.centre).localeCompare(String(b.centre)) ||
        String(b.send_date).localeCompare(String(a.send_date))
    )
  }, [rows, showAll])
  const recallable = rows.filter((r) => r.can_recall)
  const allPicked = recallable.length > 0 && picked.length === recallable.length

  // Prefill the editor only when every message picked says the same thing —
  // otherwise editing them together silently overwrites the differences.
  const sharedText = useMemo(() => {
    const texts = rows.filter((r) => picked.includes(r.uuid)).map((r) => r.text || '')
    if (!texts.length) return ''
    return texts.every((t) => t === texts[0]) ? texts[0] : ''
  }, [rows, picked])

  const toggle = (uuid) =>
    setPicked((prev) =>
      prev.includes(uuid) ? prev.filter((u) => u !== uuid) : [...prev, uuid]
    )

  const report = (result) => {
    const failed = (result?.results || []).filter((r) => !r.ok)
    if (!failed.length) {
      showToast(
        `${result.ok} message${result.ok === 1 ? '' : 's'} ${
          result.action === 'edit' ? 'updated' : 'removed'
        } in Slack`,
        'success'
      )
    } else {
      showToast(
        `${result.ok} done, ${result.failed} failed — ${failed
          .map((f) => `${f.label || f.uuid}: ${f.error}`)
          .join('; ')}`,
        'error'
      )
    }
    load()
  }

  // Under "All" the picked messages can belong to several streams; each
  // stream's ledger is asked separately and the answers are added up.
  const recallPicked = (text) => {
    if (!isAll) return recallReminders(picked, text, stream)
    const byStream = {}
    rows
      .filter((r) => picked.includes(r.uuid))
      .forEach((r) => {
        byStream[r.stream_key] = [...(byStream[r.stream_key] || []), r.uuid]
      })
    return Promise.all(
      Object.entries(byStream).map(([key, uuids]) =>
        recallReminders(uuids, text, key)
      )
    ).then((answers) => ({
      action: text ? 'edit' : 'delete',
      ok: answers.reduce((n, a) => n + (a?.ok || 0), 0),
      failed: answers.reduce((n, a) => n + (a?.failed || 0), 0),
      results: answers.flatMap((a) => a?.results || [])
    }))
  }

  const run = (text) => {
    setBusy(true)
    recallPicked(text)
      .then(report)
      .catch(() => showToast('Could not reach Slack', 'error'))
      .finally(() => {
        setBusy(false)
        setEditOpen(false)
      })
  }

  const confirmRetract = async () => {
    const { isConfirmed } = await confirmDialog({
      text:
        `This deletes ${picked.length} message${picked.length === 1 ? '' : 's'} from Slack. ` +
        'Anyone who has already read one will still have read it. The ledger keeps ' +
        'the record, and deleting an opener lets the next run open that thread again.',
      confirmButtonText: 'Yes, delete from Slack',
      options: { title: 'Delete from Slack?' }
    })
    if (isConfirmed) run(undefined)
  }

  return (
    <div>
      <div className={classes.row}>
        <span className={classes.label}>Stream</span>
        <Select
          value={stream}
          onChange={(e) => setStream(e.target.value)}
          variant="outlined"
          margin="dense"
          style={{ minWidth: 220 }}
        >
          {options.map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </div>

      <div className={classes.planBar}>
        <Button size="small" variant="outlined" style={{ textTransform: 'none' }}
          onClick={load} disabled={loading || busy}>
          Refresh
        </Button>
        <Button size="small" variant="outlined" style={{ textTransform: 'none' }}
          onClick={() => setPicked(allPicked ? [] : recallable.map((r) => r.uuid))}
          disabled={!recallable.length || busy}>
          {allPicked ? 'Clear selection' : `Select all ${recallable.length}`}
        </Button>
        <Button size="small" color="primary" variant="contained" disableElevation
          style={{ textTransform: 'none' }}
          onClick={() => {
            setEditText(sharedText)
            setEditOpen(true)
          }}
          disabled={!picked.length || busy}>
          Edit{picked.length ? ` (${picked.length})` : ''}
        </Button>
        <Button size="small" variant="outlined" style={{ textTransform: 'none' }}
          onClick={confirmRetract} disabled={!picked.length || busy}>
          Delete from Slack{picked.length ? ` (${picked.length})` : ''}
        </Button>
        <FormControlLabel
          control={
            <Switch size="small" checked={showAll}
              onChange={(e) => setShowAll(e.target.checked)} />
          }
          label={
            <Typography variant="body2" className={classes.muted}>
              Also show messages you have already deleted, and older ones Alfred
              cannot change
            </Typography>
          }
        />
        {loading || busy ? <CircularProgress size={18} /> : null}
      </div>

      <div className={`${classes.planNote} ${classes.planWarn}`}>
        Every message Gordon has posted, newest first. Tick one or more, then{' '}
        <b>Edit</b> to rewrite the wording in Slack in place (the centre sees the
        corrected text where the old one was), or <b>Delete from Slack</b> to
        remove it — anyone who already read it will still have read it. If one
        channel cannot be reached the rest still go through.
        <div style={{ marginTop: 4 }}>
          Click any message to open it in Slack, or &ldquo;open the whole
          thread&rdquo; to see that centre&apos;s entire month in one place.
          Only messages sent after this feature shipped can be changed; older
          ones say why in the last column.
        </div>
      </div>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Sent on</TableCell>
                <TableCell>Who it went to</TableCell>
                <TableCell>Which message</TableCell>
                <TableCell>Where it landed</TableCell>
                <TableCell>What it said</TableCell>
                <TableCell align="center">Can I change it?</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((r) => (
                <TableRow key={r.uuid} hover selected={picked.includes(r.uuid)}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={picked.includes(r.uuid)}
                      disabled={!r.can_recall || busy}
                      onChange={() => toggle(r.uuid)}
                    />
                  </TableCell>
                  <TableCell className={classes.nowrap}>{r.send_date}</TableCell>
                  <TableCell>
                    {r.centre}
                    {r.thread_link ? (
                      <div>
                        <a
                          className={classes.threadLink}
                          href={r.thread_link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          open the whole thread
                        </a>
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    {isAll ? (
                      <div className={classes.muted}>{r.stream}</div>
                    ) : null}
                    <Tooltip title={r.stage_help || ''} arrow>
                      <span>{r.stage_label || 'Not recorded'}</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{r.destination_label || '—'}</TableCell>
                  <TableCell>
                    {r.text && r.link ? (
                      <Tooltip title={`${r.text}\n\nClick to open this message in Slack`} arrow>
                        <a
                          className={`${classes.preview} ${classes.threadLink}`}
                          href={r.link}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {r.text}
                        </a>
                      </Tooltip>
                    ) : r.text ? (
                      <Tooltip title={r.text} arrow>
                        <span className={classes.preview}>{r.text}</span>
                      </Tooltip>
                    ) : (
                      <span className={classes.muted}>
                        Not recorded — sent before Alfred kept the wording
                      </span>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {!r.delivered ? (
                      <Tooltip title={r.error || 'This message never reached Slack'} arrow>
                        <Chip size="small" label="Never sent" className={classes.errorChip} />
                      </Tooltip>
                    ) : r.retracted ? (
                      <span className={classes.muted}>Already deleted</span>
                    ) : r.can_recall ? (
                      <Chip size="small" label="Yes — still in Slack"
                        className={classes.slackChip} />
                    ) : (
                      <span className={classes.muted}>
                        No — {r.recall_blocked_because}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!shown.length && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <Typography variant="body2" className={classes.muted}>
                      {rows.length
                        ? 'Nothing here can be recalled — turn on the toggle above to see the rest.'
                        : 'Nothing has been sent yet.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Rewrite {picked.length} message{picked.length === 1 ? '' : 's'}
        </DialogTitle>
        <DialogContent>
          {!sharedText && picked.length > 1 ? (
            <div className={`${classes.planNote} ${classes.planStop}`}>
              The messages you picked do not all say the same thing. Saving
              replaces every one of them with the text below.
            </div>
          ) : null}
          <TextField
            multiline
            minRows={10}
            fullWidth
            variant="outlined"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button color="primary" disabled={!editText.trim() || busy}
            onClick={() => run(editText.trim())}>
            Update in Slack
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}



// The whole cycle as one list, a line per morning. The per-day preview answers
// "what goes out tomorrow"; this answers "what does this month look like",
// which is the question you ask once and then act on. History is real (it comes
// from the ledger); everything from today on is a projection, and says so —
// a centre submitting tomorrow deletes its own chase.
const CycleRail = ({ cycle, onPickDay }) => {
  const classes = useStyles()
  const days = cycle?.days || []
  if (!days.length) {
    return (
      <div className={classes.planQuiet}>
        Nothing scheduled for this cycle yet. It opens on{' '}
        {dayjs(cycle?.opens).format('D MMM')}.
      </div>
    )
  }
  let phase = null
  return (
    <Paper variant="outlined">
      {days.map((day) => {
        const header = day.phase !== phase ? day.phase : null
        phase = day.phase
        const failed = day.items.reduce((n, i) => n + (i.failed || 0), 0)
        return (
          <React.Fragment key={day.date}>
            {header ? (
              <div className={classes.railPhase}>
                {header === 'ordering'
                  ? `Ordering — to the ${dayjs(cycle.deadline).format(
                      'D MMM'
                    )} deadline`
                  : 'Delivery — confirming what arrived'}
              </div>
            ) : null}
            <button
              type="button"
              className={`${classes.railDay} ${
                day.today ? classes.railToday : ''
              }`}
              onClick={() => onPickDay(day.date)}
              title="Open this morning in full"
            >
              <span className={classes.railDate}>
                <b>{dayjs(day.date).format('ddd D')}</b>{' '}
                {dayjs(day.date).format('MMM')}
                {day.today ? ' · today' : ''}
              </span>
              <span
                className={`${classes.railDot} ${
                  day.actual || day.today
                    ? classes.railSent
                    : classes.railPlanned
                }`}
              />
              <span className={classes.railWhat}>
                {day.items.map((item) => item.label).join(' · ')}
              </span>
              <span className={classes.railCount}>
                {day.actual || day.today
                  ? `${day.total} sent`
                  : `up to ${day.total}`}
                {failed ? (
                  <span className={classes.railFailed}> · {failed} failed</span>
                ) : null}
              </span>
            </button>
          </React.Fragment>
        )
      })}
    </Paper>
  )
}

// One stream's "what goes out that morning" — used once per stream under All.
const DayPlan = ({ plan }) => {
  const classes = useStyles()
  const messages = plan?.messages || []
  // Streams answer "what is about to go out" in their own shape: a monthly
  // cycle by message, a daily chase by centre. `kind` says which.
  const isDaily = plan?.kind === 'daily'
  return (
    <>
      {!plan ? (
        <Paper className={classes.section}>
          <div className={classes.planQuiet}>
            This stream has no forward view — it sends one message on its own
            schedule. Use <b>How this works</b> on the Controls tab to see
            exactly what it says.
          </div>
        </Paper>
      ) : null}

      {isDaily ? (
        <Paper className={classes.section}>
          <Typography className={classes.sectionTitle}>
            Going out at {plan.send_at} — visits of {plan.date}
          </Typography>
          <div className={classes.planQuiet}>{plan.note}</div>
          {(plan.rows || []).length === 0 ? (
            <div className={classes.planQuiet}>
              Nothing pending right now, so no centre would be messaged.
            </div>
          ) : (
            (plan.rows || []).map((row) => (
              <Paper key={row.centre} variant="outlined" style={{ marginBottom: 12 }}>
                <Typography className={classes.metaLine} style={{ padding: '8px 12px 0' }}>
                  <strong>{row.centre}</strong> — {row.pending} of {row.attended}{' '}
                  who attended still unlogged · to {row.destination_label}
                </Typography>
                <pre className={classes.msgText}>{row.text}</pre>
              </Paper>
            ))
          )}
        </Paper>
      ) : null}

      {!isDaily && plan ? (
        <>
          {plan.blocked ? (
            <div className={`${classes.planNote} ${classes.planStop}`}>
              <b>Nothing will be sent — {plan.blocked}.</b> This is what the run
              would say if the engine were on.
            </div>
          ) : null}

          {plan.override ? (
            <div className={`${classes.planNote} ${classes.planWarn}`}>
              <b>Test mode.</b> Every message below goes to <b>{plan.override}</b>,
              not to the channel named on it. Clearing the override sends the
              budget thread <i>and</i> all centre threads for real.
            </div>
          ) : (
            <div className={`${classes.planNote} ${classes.planOk}`}>
              <b>Live.</b> These messages will reach the real channels shown.
            </div>
          )}

          {!plan.gate_clear ? (
            <div className={`${classes.planNote} ${classes.planWarn}`}>
              Budgets are not in for <b>{plan.order_month}</b>, so no centre is
              messaged yet. Waiting on:{' '}
              <b>{(plan.missing_budgets || []).join(', ') || '—'}</b>
            </div>
          ) : null}

          <div className={`${classes.planNote} ${classes.planWarn}`}>
            <b>{(plan.centres_in_scope || []).length} centres in scope:</b>{' '}
            {(plan.centres_in_scope || []).join(', ') || 'none'}
            <div style={{ marginTop: 4 }}>
              Scope is the centres with a mapped Slack channel. A centre missing
              from this list is never messaged and never holds the budget gate —
              it is invisible rather than broken, so check the list matches the
              centres you expect.
            </div>
          </div>

          {(plan.unmapped_channel_keys || []).length ? (
            <div className={`${classes.planNote} ${classes.planStop}`}>
              <b>A mapped channel matches no centre:</b>{' '}
              {(plan.unmapped_channel_keys || []).join(', ')}
              <div style={{ marginTop: 4 }}>
                Scope is joined on the centre name, so a name that does not
                match exactly drops that centre out of the cycle silently. Fix
                the key to Alfred&apos;s spelling of the centre name.
              </div>
            </div>
          ) : null}

          <Typography className={classes.sectionTitle}>
            {messages.length === 0
              ? 'Nothing goes out'
              : `${messages.length} message${messages.length === 1 ? '' : 's'} on ${plan.date}`}
            {' · '}order month {plan.order_month} · deadline {plan.deadline}
          </Typography>

          {messages.length === 0 ? (
            <Paper className={classes.section}>
              <div className={classes.planQuiet}>
                A quiet morning. Silence is the default — the engine only speaks
                when a participant is actually owed something.
              </div>
            </Paper>
          ) : null}

          {messages.map((m) => (
            <div className={classes.msgBlock} key={`${m.audience}-${m.stage}`}>
              <div className={classes.msgHead}>
                <span className={classes.msgTitle}>{m.audience}</span>
                <Chip size="small" label={m.stage} className={classes.slackChip} />
                <span className={classes.msgWhen}>
                  {m.unmapped ? 'no channel mapped — will NOT send' : `→ ${m.destination}`}
                </span>
              </div>
              <pre className={classes.msgText}>{m.text}</pre>
            </div>
          ))}
        </>
      ) : null}
    </>
  )
}

const PLAN_STREAM = 'program_material'

const PlanTab = ({ plugins }) => {
  const classes = useStyles()
  const options = plugins || []
  const [stream, setStream] = useState(ALL_STREAMS)
  const isAll = stream === ALL_STREAMS
  const [planDate, setPlanDate] = useState(dayjs().format('YYYY-MM-DD'))
  const [plan, setPlan] = useState(null)
  // Under All: one entry per stream, `plan` null when it has no forward view.
  const [allPlans, setAllPlans] = useState([])
  const [cycle, setCycle] = useState(null)
  const [view, setView] = useState('cycle')
  const [loading, setLoading] = useState(false)

  const load = useCallback((d, key) => {
    setLoading(true)
    setPlan(null)
    if (key === ALL_STREAMS) {
      setAllPlans([])
      Promise.all(
        options.map((opt) =>
          fetchReminderPlan(d, opt.key)
            .catch(() => null)
            .then((one) => ({ key: opt.key, label: opt.label, plan: one }))
        )
      )
        .then(setAllPlans)
        .finally(() => setLoading(false))
      return
    }
    fetchReminderPlan(d, key)
      .then(setPlan)
      .catch(() => showToast('Could not load what is going out next', 'error'))
      .finally(() => setLoading(false))
  }, [options])

  const loadCycle = useCallback((key) => {
    setLoading(true)
    setCycle(null)
    fetchReminderCycle('', key)
      .then(setCycle)
      .catch(() => showToast('Could not load the cycle', 'error'))
      .finally(() => setLoading(false))
  }, [])

  // The whole-cycle rail is Program Material's own; All shows one morning of
  // every stream.
  const showView = isAll ? 'day' : view

  useEffect(() => {
    if (showView === 'cycle') loadCycle(stream)
    else load(planDate, stream)
  }, [showView, load, loadCycle, planDate, stream])

  return (
    <div>
      <div className={classes.planBar}>
        <Select
          value={stream}
          onChange={(e) => setStream(e.target.value)}
          variant="outlined"
          margin="dense"
          style={{ minWidth: 220 }}
        >
          {withAll(
            options.length
              ? options
              : [{ key: PLAN_STREAM, label: 'Program Material' }]
          ).map((opt) => (
            <MenuItem key={opt.key} value={opt.key}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
        <div style={isAll ? { display: 'none' } : undefined}>
          <Button
            size="small"
            variant={showView === 'cycle' ? 'contained' : 'outlined'}
            color={showView === 'cycle' ? 'primary' : 'default'}
            disableElevation
            onClick={() => setView('cycle')}
            style={{
              textTransform: 'none',
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0
            }}
          >
            Whole cycle
          </Button>
          <Button
            size="small"
            variant={showView === 'day' ? 'contained' : 'outlined'}
            color={showView === 'day' ? 'primary' : 'default'}
            disableElevation
            onClick={() => setView('day')}
            style={{
              textTransform: 'none',
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              marginLeft: -1
            }}
          >
            One morning
          </Button>
        </div>
        {showView === 'day' ? (
          <TextField
            type="date"
            size="small"
            variant="outlined"
            label="Morning of"
            value={planDate}
            onChange={(e) => setPlanDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        ) : null}
        <Button
          size="small"
          onClick={() =>
            showView === 'cycle' ? loadCycle(stream) : load(planDate, stream)
          }
          disabled={loading}
        >
          Refresh
        </Button>
        {loading ? <CircularProgress size={18} /> : null}
      </div>

      {showView === 'cycle' ? (
        <div>
          {!loading && cycle ? (
            <>
              <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
                {cycle.order_month} cycle · opens{' '}
                {dayjs(cycle.opens).format('D MMM')} · deadline{' '}
                {dayjs(cycle.deadline).format('D MMM')} · hands over to the next
                cycle on {dayjs(cycle.ends).format('D MMM')}
              </Typography>
              <CycleRail
                cycle={cycle}
                onPickDay={(d) => {
                  setPlanDate(d)
                  setView('day')
                }}
              />
              <div className={classes.planQuiet}>
                {cycle.projection_note} Click any morning to see the exact
                messages.
              </div>
            </>
          ) : null}
          {!loading && !cycle ? (
            <div className={classes.planQuiet}>Could not load the cycle.</div>
          ) : null}
        </div>
      ) : null}

      {showView === 'day' && !loading && isAll
        ? allPlans.map(({ key, label, plan: one }) => (
            <div key={key} style={{ marginBottom: 24 }}>
              <Typography className={classes.sectionTitle}>{label}</Typography>
              <DayPlan plan={one} />
            </div>
          ))
        : null}
      {showView === 'day' && !loading && !isAll ? <DayPlan plan={plan} /> : null}
    </div>
  )
}

// ---- Page ------------------------------------------------------------------

const RemindersPage = () => {
  const classes = useStyles()
  const [tab, setTab] = useState('controls')
  const [plugins, setPlugins] = useState([])

  // The audit tab needs the plugin list for its dropdown; fetch it once so the
  // dropdown is populated regardless of which tab loads first.
  useEffect(() => {
    let alive = true
    fetchReminderControls()
      .then((data) => {
        if (alive) setPlugins(data.plugins || [])
      })
      .catch(() => {})
    return () => {
      alive = false
    }
  }, [])

  return (
    <div className={classes.root}>
      <Typography className={classes.title}>Gordon — reminder engine</Typography>
      <Typography className={classes.subtitle}>
        One always-on engine, one card per stream. Turn streams on or off, edit
        their message, send a test, and review what was sent.
      </Typography>
      <Tabs
        value={tab}
        onChange={(e, v) => setTab(v)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tabsBar}
      >
        <Tab value="controls" label="Controls" style={{ textTransform: 'none' }} />
        <Tab value="plan" label="What's next" style={{ textTransform: 'none' }} />
        <Tab value="sent" label="Already sent" style={{ textTransform: 'none' }} />
        <Tab value="audit" label="Agent Audit" style={{ textTransform: 'none' }} />
      </Tabs>

      {tab === 'controls' ? <ControlsTab /> : null}
      {tab === 'plan' ? <PlanTab plugins={plugins} /> : null}
      {tab === 'sent' ? <SentTab plugins={plugins} /> : null}
      {tab === 'audit' ? <AuditTab plugins={plugins} /> : null}
    </div>
  )
}

export default RemindersPage
